/**
 * Communications Page Types
 *
 * Story 3.5.4: Communications Page (Sales Rep Only)
 *
 * Types for the unified activity hub showing meetings and emails
 * across all clients for sales representatives.
 *
 * NOTE: Tasks are explicitly excluded as they are open-ended and cannot be assigned time.
 */
import { z } from 'zod';
// Import MeetingStatus from meeting-analysis to avoid duplicate export
import { MeetingStatus } from './meeting-analysis.js';
import { MeetingSessionSchema } from './meeting-detail.js';
import { DEAL_MATCH_STRATEGIES } from './deal-match-strategy.js';
import { TodaysMeetingStatus } from './meetings-page.js';
// Re-export for backwards compatibility within this module's consumers
export { MeetingStatus };
// =============================================================================
// Enums & Constants
// =============================================================================
export const ActivityType = {
    MEETING: 'meeting',
    EMAIL: 'email',
};
export const ActivityFilter = {
    ALL: 'all',
    MEETINGS: 'meetings',
    EMAILS: 'emails',
    UNLINKED: 'unlinked',
};
// Note: EmailProvider is NOT exported here to avoid conflict with email.ts
// Use inline 'gmail' | 'outlook' in schemas instead
// =============================================================================
// Zod Schemas
// =============================================================================
export const MeetingActivityDataSchema = z.object({
    attendees: z.array(z.object({
        name: z.string(),
        email: z.string().email(),
        avatar: z.string().url().nullable().optional(),
    })),
    startTime: z.string(),
    endTime: z.string().nullable().optional(),
    meetingUrl: z.string().url().nullable().optional(),
    status: z.enum(Object.values(TodaysMeetingStatus)),
    sourceStatus: z.enum(['active', 'deleted']),
    sourceDeletedAt: z.string().nullable(),
    calendarProvider: z.enum(['google', 'outlook']).nullable(),
    scheduledStart: z.string().nullable().optional(),
    scheduledEnd: z.string().nullable().optional(),
    durationMs: z.number().int().nullable().optional(),
    sessionCount: z.number().int().min(0).optional(),
    /** Recording sessions for this meeting; empty array means no recording yet. */
    sessions: z.array(MeetingSessionSchema),
    /**
     * True when the source calendar event has no clock time (all-day block).
     * startTime/endTime are UTC midnight bounds in that case; the UI must
     * render "All day" rather than formatting the clock value.
     */
    isAllDay: z.boolean().optional(),
    /**
     * Phase E.1: true iff a `memory_extraction_payloads` row exists for this
     * meeting - lets the UI conditionally show a "Backfill memory" affordance
     * without a per-row fetch. Optional for back-compat with older clients.
     */
    hasMemoryPayload: z.boolean().optional(),
});
export const EmailActivityDataSchema = z.object({
    sender: z.object({
        name: z.string(),
        email: z.string().email(),
        avatar: z.string().url().nullable().optional(),
    }),
    preview: z.string(),
    threadCount: z.number(),
    isUnread: z.boolean(),
    emailProvider: z.enum(['gmail', 'outlook']).nullable(),
});
export const SuggestedClientSchema = z.object({
    id: z.string(),
    name: z.string(),
    logoUrl: z.string().url().nullable().optional(),
    confidence: z.number().min(0).max(1),
});
export const ClientRefSchema = z.object({
    id: z.string(),
    name: z.string(),
    logoUrl: z.string().url().nullable().optional(),
    lifecycle: z.enum(['active', 'archived', 'deleted']).optional(),
});
export const DealRefSchema = z.object({
    id: z.string(),
    name: z.string(),
    stage: z.string(),
    lifecycle: z.enum(['open', 'closed', 'archived', 'deleted']).optional(),
});
export const CommunicationsActivityItemSchema = z.object({
    id: z.string(),
    type: z.enum(Object.values(ActivityType)),
    title: z.string(),
    timestamp: z.string(),
    client: ClientRefSchema.nullable().optional(),
    deal: DealRefSchema.nullable().optional(),
    dealMatchStrategy: z.enum(DEAL_MATCH_STRATEGIES).nullable().optional(),
    dealConfidenceScore: z.number().min(0).max(100).nullable().optional(),
    dealLinkReason: z.string().nullable().optional(),
    isLinked: z.boolean(),
    actionItemIds: z.array(z.string()).optional(),
    suggestedClient: SuggestedClientSchema.nullable().optional(),
    meetingData: MeetingActivityDataSchema.nullable().optional(),
    emailData: EmailActivityDataSchema.nullable().optional(),
});
export const CommunicationsDateGroupSchema = z.object({
    date: z.string(), // ISO date YYYY-MM-DD
    label: z.string(), // "Today", "Tomorrow", "Jan 20", etc.
    items: z.array(CommunicationsActivityItemSchema),
});
export const CommunicationsCountsSchema = z.object({
    all: z.number(),
    meetings: z.number(),
    emails: z.number(),
    unlinked: z.number(),
});
export const CommunicationsFeedResponseSchema = z.object({
    groups: z.array(CommunicationsDateGroupSchema),
    hasMore: z.boolean(),
    nextCursor: z.string().nullable(),
    counts: CommunicationsCountsSchema,
});
export const CalendarEventSummarySchema = z.object({
    date: z.string(), // ISO date YYYY-MM-DD
    meetingCount: z.number(),
    emailCount: z.number(),
    hasMeeting: z.boolean(),
    hasEmail: z.boolean(),
    hasUnlinked: z.boolean().optional(),
});
export const CalendarSummaryResponseSchema = z.object({
    events: z.array(CalendarEventSummarySchema),
});
export const TomorrowOutlookDataSchema = z.object({
    meetings: z.number(),
    emailsToFollowUp: z.number(),
    items: z.array(CommunicationsActivityItemSchema),
});
export const LinkActivityRequestSchema = z.object({
    activityId: z.string(),
    activityType: z.enum(Object.values(ActivityType)),
    clientId: z.string(),
    dealId: z.string().optional(),
    replaceExisting: z.boolean().optional(),
});
export const CommunicationsFeedQuerySchema = z.object({
    type: z.enum(Object.values(ActivityFilter)).optional(),
    dateFrom: z.string().optional(),
    dateTo: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.coerce.number().min(1).max(100).optional(),
});
export const ActivityDealSuggestionSchema = z.object({
    dealId: z.string().uuid(),
    dealName: z.string(),
    value: z.number().nullable(),
    stage: z.string(),
    confidence: z.number().min(0).max(1),
    matchReason: z.string(),
});
export const ClientMatchSuggestionSchema = z.object({
    clientId: z.string().uuid(),
    clientName: z.string(),
    logoUrl: z.string().url().nullable(),
    lifecycle: z.enum(['active', 'archived', 'deleted']).optional(),
    confidence: z.number().min(0).max(1),
    matchReason: z.string(),
    suggestedDeal: ActivityDealSuggestionSchema.nullish(),
});
export const MatchSuggestionResponseSchema = z.object({
    activityId: z.string(),
    activityType: z.enum(Object.values(ActivityType)),
    suggestions: z.array(ClientMatchSuggestionSchema),
});
